import { useNavigate } from '@solidjs/router';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { bankAccountRepository } from '~/repositories/bankAccountRepository';
import { companyRepository } from '~/repositories/companiesRepository';
import { meetingRepository } from '~/repositories/meetingRepository';
import { onboardingRepository } from '~/repositories/onboardingRepository';
import { phonesRepository } from '~/repositories/phones';
import { OnboardingStatus } from '~/utils/constant';
import { createLazyResource, createMutation } from '~/utils/resource';
import type { CompanyStatusDto, CreateMeetingScheduleDto, NotificationScheduleDto, UpdateBankAccountDto } from '~/swagger/Api';

export const [OnboardingProvider, useOnboarding] = createMagicDoorContext('Onboarding', () => {
  const navigate = useNavigate();
  const [companies, { mutate: mutateCompanies }] = createLazyResource(async () => {
    const _companies = await companyRepository.getCompanies();
    const _status = _companies.onboardingStatus || OnboardingStatus.GET_VERIFIED_CHECKLIST;
    return {
      ..._companies,
      onboardingStatus: _status,
    };
  });

  const [canGoNext, setCanGoNext] = createSignal<boolean>(true);

  const [selectedNotification, setSelectedNotification] = createSignal<NotificationScheduleDto | null>(null);

  const [companyStatus, { mutate: mutateCompanyStatus }] = createLazyResource(async () => {
    return await companyRepository.getCompanyStatus();
  });

  const [companyCanBeChanged] = createLazyResource(async () => {
    return await companyRepository.getCompanyCanBeChanged();
  });

  const [plannedNotifications, { refetch: refetchPlannedNotifications }] = createLazyResource(async () => {
    try {
      const response = await onboardingRepository.getPlannedNotifications();
      return response.items;
    } catch (error) {
      console.error('Error fetching planned notifications:', error);
      return [];
    }
  });

  const [scheduledNotificationFullData] = createLazyResource(selectedNotification, async (notification) => {
    if (!notification) return null;
    return await onboardingRepository.getNotificationSchedule(notification.id);
  });

  const isOnboardingCompleted = createMemo(() => {
    return companies()?.onboarded;
  });

  const [bankAccounts, { refetch: refetchBankAccounts }] = createLazyResource(async () => {
    return await bankAccountRepository.getBankAccounts();
  });

  const [scheduledMeetings, { refetch: refetchScheduledMeetings }] = createLazyResource(async () => {
    return await meetingRepository.getMeetingSchedules();
  });

  const isAllVerificationCompleted = createMemo(() => {
    const _companyStatus = companyStatus();
    if (!_companyStatus) return false;
    return Object.values(_companyStatus).every((status) => status === true);
  });

  const isNotificationsClean = createMemo(() => {
    const notifications = plannedNotifications();
    return notifications?.length && notifications?.every((n) => n.status === 'sent');
  });

  const isOnboardingCompletedAndWithoutUnsentNotifications = createMemo(() => {
    return isOnboardingCompleted() && isNotificationsClean();
  });

  const shouldHideOnboardingMenu = createMemo(() => {
    return companies()?.onboardingStatus === OnboardingStatus.USER_HIDE_ONBOARDING || !companies();
  });

  const isStepCompleted = (item: keyof CompanyStatusDto | (keyof CompanyStatusDto)[]) => {
    if (Array.isArray(item)) {
      return item.every((id) => companyStatus()?.[id]);
    }
    return companyStatus()?.[item];
  };

  // actions

  const unScheduledNotification = createMutation(async () => {
    const _id = selectedNotification()?.id;
    if (!_id) return;

    await onboardingRepository.deleteNotificationSchedule(_id);
    refetchPlannedNotifications();
    setSelectedNotification(null);
  });

  const debugSendNotification = createMutation(async (scheduleId: string) => {
    if (!import.meta.env.DEV) return false;
    try {
      await onboardingRepository.debugSendNotification(scheduleId);
      return true;
    } catch (error) {
      console.error('Failed to send debug notification:', error);
      return false;
    }
  });

  const setCompanyPhoneNumber = createMutation(async (phoneNumber: string) => {
    await phonesRepository.updateCompanyPhoneNumber(phoneNumber);
  });

  const debugSendAllNotifications = createMutation(async () => {
    if (!import.meta.env.DEV) return false;
    try {
      const notifications = plannedNotifications();
      if (!notifications) return false;

      const unsentNotifications = notifications.filter((n) => n.status !== 'sent');
      await Promise.all(unsentNotifications.map((n) => onboardingRepository.debugSendNotification(n.id)));
      return true;
    } catch (error) {
      console.error('Failed to send all debug notifications:', error);
      return false;
    }
  });

  const updateOnboardingStep = (status: OnboardingStatus) => {
    mutateCompanies((prev) => (prev ? { ...prev, onboardingStatus: status } : prev));
    onboardingRepository.updateOnboardingStep(status);
  };

  const completeOnboarding = createMutation(async () => {
    await Promise.all([onboardingRepository.completeOnboarding(), onboardingRepository.updateOnboardingStep(OnboardingStatus.PAID)]);
    mutateCompanies((prev) => (prev ? { ...prev, onboarded: true, onboardingStatus: OnboardingStatus.PAID } : prev));
  });

  const hideOnboarding = createMutation(async () => {
    await onboardingRepository.updateOnboardingStep(OnboardingStatus.USER_HIDE_ONBOARDING);
    mutateCompanies((prev) => (prev ? { ...prev, onboardingStatus: OnboardingStatus.USER_HIDE_ONBOARDING } : prev));
    navigate('/');
  });

  const updateCompanyStatus = (status: keyof CompanyStatusDto) => {
    mutateCompanyStatus((prev) => (prev ? { ...prev, [status]: true } : prev));
  };

  const bankAccountOptions = createMemo(() => {
    return bankAccounts()?.map((bankAccount) => ({ label: bankAccount.name, value: bankAccount.id })) || [];
  });

  const handleCreateMeetingSchedule = createMutation(async (schedule: CreateMeetingScheduleDto) => {
    await meetingRepository.createMeetingSchedule(schedule);
    await refetchScheduledMeetings();
  });

  const updateBankAccount = createMutation(async (id: string, bankAccount: UpdateBankAccountDto) => {
    await bankAccountRepository.updateBankAccount(id, bankAccount);
  });

  return {
    companies,
    setCompanyPhoneNumber,
    plannedNotifications,
    refetchPlannedNotifications,
    updateOnboardingStep,
    updateCompanyStatus,
    completeOnboarding,
    companyStatus,
    companyCanBeChanged,
    isOnboardingCompleted,
    isOnboardingCompletedAndWithoutUnsentNotifications,
    bankAccounts,
    refetchBankAccounts,
    bankAccountOptions,

    debugSendNotification,
    debugSendAllNotifications,
    unScheduledNotification,
    scheduledNotificationFullData,
    setSelectedNotification,

    scheduledMeetings,
    handleCreateMeetingSchedule,
    selectedNotification,

    canGoNext,
    setCanGoNext,
    updateBankAccount,
    isAllVerificationCompleted,
    hasUnsentNotifications: () => !isNotificationsClean(),
    isStepCompleted,
    shouldHideOnboardingMenu,
    hideOnboarding,
  };
});
